import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Form, Table } from 'react-bootstrap';
import axios from 'axios';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const DAY = 24 * 3600 * 1000;
const SOON_DAYS = 14;

export const planBadge = (org) => {
  if (!org?.subscriptionPlan) return <Badge bg="secondary">No plan</Badge>;
  const status = org.subscriptionStatus || 'N/A';
  return <Badge bg={status === 'ACTIVE' ? 'success' : status === 'TRIAL' ? 'info' : status === 'EXPIRED' ? 'danger' : 'secondary'}>{status}</Badge>;
};

/** Whole days from now to the plan's end (negative once it has passed); null when there is no end date. */
const daysToEnd = (org, now) => (org.subscriptionEndDate ? Math.ceil((new Date(org.subscriptionEndDate).getTime() - now) / DAY) : null);

// Days are counted on the calendar in the viewer's own zone (the last active time reaches the browser with the server's offset, so it is the
// right moment here): yesterday is the day before today, not "between 24 and 48 hours ago".
const ago = (iso, now) => {
  if (!iso) return 'Never';
  const then = new Date(iso);
  const today = new Date(now);
  const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((midnight(today) - midnight(then)) / DAY);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

const FILTERS = [
  ['ALL', 'All plans'], ['TRIAL', 'On trial'], ['ACTIVE', 'Paying'], ['EXPIRED', 'Expired'], ['NONE', 'No plan'], ['SOON', `Ending within ${SOON_DAYS} days`],
];

/**
 * The organizations list for System Admin and Tech Support: find an organization (search, and a plan filter), see its plan and status,
 * when the plan ends, how many people use it, when it was last used and when it signed up, and open it. The headers sort. The figures
 * for people and activity come from one read-only call.
 */
function OrganizationsTable({ organizations, openLabel = 'View', onOpen, headerRight = null }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [sort, setSort] = useState({ key: 'created', dir: 'desc' });
  const [figures, setFigures] = useState({});
  const now = Date.now();

  useEffect(() => {
    axios.get('/api/system-admin/organizations/summary')
      .then((r) => setFigures(Object.fromEntries((r.data || []).map((x) => [x.organizationId, x]))))
      .catch(() => setFigures({}));
  }, [organizations.length]);

  const counts = useMemo(() => {
    const c = { TRIAL: 0, ACTIVE: 0, EXPIRED: 0, NONE: 0 };
    organizations.forEach((o) => { c[!o.subscriptionPlan ? 'NONE' : (o.subscriptionStatus in c ? o.subscriptionStatus : 'NONE')] += 1; });
    return c;
  }, [organizations]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const t = Date.now();
    const keep = organizations.filter((o) => {
      if (q && !`${o.id} ${o.name} ${o.email || ''} ${o.country || ''}`.toLowerCase().includes(q)) return false;
      if (filter === 'ALL') return true;
      if (filter === 'NONE') return !o.subscriptionPlan;
      if (filter === 'SOON') { const d = daysToEnd(o, t); return o.subscriptionPlan && o.subscriptionStatus !== 'EXPIRED' && d !== null && d <= SOON_DAYS; }
      return o.subscriptionPlan && o.subscriptionStatus === filter;
    });
    const value = {
      name: (o) => String(o.name || '').toLowerCase(),
      created: (o) => (o.createdAt ? new Date(o.createdAt).getTime() : 0),
      ends: (o) => (o.subscriptionEndDate ? new Date(o.subscriptionEndDate).getTime() : Number.MAX_SAFE_INTEGER),
      users: (o) => figures[o.id]?.users ?? -1,
      last: (o) => (figures[o.id]?.lastActiveAt ? new Date(figures[o.id].lastActiveAt).getTime() : 0),
    }[sort.key];
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...keep].sort((a, b) => (value(a) < value(b) ? -dir : value(a) > value(b) ? dir : 0));
  }, [organizations, search, filter, sort, figures]);

  const head = (key, label, className) => (
    <th className={className} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}>
      {label}{sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  return (
    <div data-testid="organizations-table">
      <div className="d-flex justify-content-between align-items-center mb-2 gap-3 flex-wrap">
        <h5 className="mb-0">Organizations</h5>
        <div className="d-flex gap-2 flex-wrap">
          <Form.Control type="search" style={{ width: 260 }} placeholder="Search name, email, country or ID" aria-label="Search organizations"
            value={search} onChange={(e) => setSearch(e.target.value)} />
          <Form.Select style={{ width: 210 }} aria-label="Filter by plan" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="org-filter">
            {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Form.Select>
          {headerRight}
        </div>
      </div>
      <div className="small text-muted mb-2" data-testid="org-counts">
        {organizations.length} organization{organizations.length === 1 ? '' : 's'}: {counts.TRIAL} on trial, {counts.ACTIVE} paying, {counts.EXPIRED} expired
        {counts.NONE > 0 ? `, ${counts.NONE} with no plan` : ''}
        {rows.length !== organizations.length ? ` · showing ${rows.length}` : ''}
      </div>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>ID</th>
            {head('name', 'Organization')}
            <th>Plan</th>
            <th>Status</th>
            {head('ends', 'Plan ends')}
            {head('users', 'Users', 'text-end')}
            {head('last', 'Last active')}
            {head('created', 'Signed up')}
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={9} className="text-center text-muted">No organization matches</td></tr>}
          {rows.map((o) => {
            const days = daysToEnd(o, now);
            const live = o.subscriptionPlan && o.subscriptionStatus !== 'EXPIRED';
            const f = figures[o.id];
            return (
              <tr key={o.id}>
                <td>{o.id}</td>
                <td>
                  <div className="fw-semibold">{o.name}</div>
                  <div className="small text-muted">{[o.email, o.country].filter(Boolean).join(' · ')}</div>
                </td>
                <td>{o.subscriptionPlan?.name || '—'}</td>
                <td>{planBadge(o)}</td>
                <td className="text-nowrap">
                  {o.subscriptionEndDate ? (
                    <>
                      {formatDate(o.subscriptionEndDate)}
                      {days !== null && o.subscriptionPlan && (days < 0 || o.subscriptionStatus === 'EXPIRED'
                        ? <div className="small text-danger">ended {Math.max(1, -days)} day{-days === 1 ? '' : 's'} ago</div>
                        : live && days <= SOON_DAYS && <div className="small text-warning">{days <= 0 ? 'ends today' : `in ${days} day${days === 1 ? '' : 's'}`}</div>)}
                    </>
                  ) : '—'}
                </td>
                <td className="text-end">{f ? f.users : '—'}</td>
                <td className="text-nowrap" title={f?.lastActiveAt ? formatDateTime(f.lastActiveAt) : ''}>{f ? ago(f.lastActiveAt, now) : '—'}</td>
                <td className="text-nowrap">{o.createdAt ? formatDate(o.createdAt) : '—'}</td>
                <td className="text-end text-nowrap">
                  <Button size="sm" variant="outline-primary" onClick={() => onOpen(o)}>{openLabel}</Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

export default OrganizationsTable;
